import { useState, useEffect } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface DataPoint {
  label: string;
  total: number;
  avg: number | null;
}

interface MonthlyDataPoint extends DataPoint {
  tooltipLabel: string;
}

function tooltipFormatter(value: number | undefined, name: string | undefined) {
  return [`${(value ?? 0).toFixed(2)} units`, name];
}

function AvgLegend() {
  return (
    <span className="flex items-center gap-1.5 text-xs text-gray-400">
      <span className="inline-block w-3 h-0.5 bg-amber-500" />
      3-mo avg
    </span>
  );
}

const WEEKLY_RANGES = [3, 6, 12] as const;
type WeeklyRange = (typeof WEEKLY_RANGES)[number];

export function ReportsPage() {
  const [monthlyData, setMonthlyData] = useState<MonthlyDataPoint[] | null>(null);
  const [weeklyData, setWeeklyData] = useState<DataPoint[] | null>(null);
  const [weeklyRange, setWeeklyRange] = useState<WeeklyRange>(6);

  useEffect(() => {
    fetch("/api/reports/monthly?months=24")
      .then((r) => r.json())
      .then(setMonthlyData)
      .catch(() => setMonthlyData([]));
  }, []);

  useEffect(() => {
    fetch(`/api/reports/weekly?months=${weeklyRange}`)
      .then((r) => r.json())
      .then(setWeeklyData)
      .catch(() => setWeeklyData([]));
  }, [weeklyRange]);

  return (
    <div className="max-w-lg mx-auto p-4">
      {/* Monthly chart */}
      <div className="bg-[#16213e] rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm uppercase tracking-wide text-gray-400">
            Monthly Totals
          </h2>
          <AvgLegend />
        </div>
        {monthlyData === null ? (
          <div className="h-52 bg-[#0f3460] rounded-lg animate-pulse" />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0f3460" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "#9ca3af", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#0f3460" }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: "#9ca3af", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={35}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1a1a2e",
                  border: "1px solid #0f3460",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 13,
                }}
                labelFormatter={(_label, payload) => {
                  const item = payload?.[0]?.payload as MonthlyDataPoint | undefined;
                  return item?.tooltipLabel ?? _label;
                }}
                formatter={tooltipFormatter}
                cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
              />
              <Bar
                dataKey="total"
                name="Total"
                fill="#3b82f6"
                radius={[3, 3, 0, 0]}
              />
              <Line
                dataKey="avg"
                name="3-mo avg"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Weekly chart */}
      <div className="bg-[#16213e] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="text-sm uppercase tracking-wide text-gray-400">
              Weekly Totals
            </h2>
            <AvgLegend />
          </div>
          <div className="flex gap-1">
            {WEEKLY_RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setWeeklyRange(r)}
                className={`text-xs px-2.5 py-1 rounded-lg transition-colors cursor-pointer ${
                  weeklyRange === r
                    ? "bg-blue-600 text-white"
                    : "bg-[#0f3460] text-gray-400 hover:text-white"
                }`}
              >
                {r}mo
              </button>
            ))}
          </div>
        </div>
        {weeklyData === null ? (
          <div className="h-52 bg-[#0f3460] rounded-lg animate-pulse" />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0f3460" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "#9ca3af", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#0f3460" }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: "#9ca3af", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={35}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1a1a2e",
                  border: "1px solid #0f3460",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 13,
                }}
                labelFormatter={(label) => `Week of ${label}`}
                formatter={tooltipFormatter}
                cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
              />
              <Bar
                dataKey="total"
                name="Total"
                fill="#3b82f6"
                radius={[3, 3, 0, 0]}
              />
              <Line
                dataKey="avg"
                name="3-mo avg"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
